import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApplicationTrackerContext } from '../../contexts/ApplicationTrackerContext';
import axiosInstance from '../../utils/axiosConfig';
import AddApplicationModal from '../ApplicationTracker/AddApplicationModal';
import OnGlance from './OnGlance';
import ExternalLink from '../common/ExternalLink';

function JobComponent({ role, tutorialRefs }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAddApplicationModalOpen, setisAddApplicationModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { currentApps } = useContext(ApplicationTrackerContext);

  // Check if this role is already in user's applications
  const hasApplied = currentApps?.some((app) => {
    const isMatchingJob =
      app.role_id === role.role_id ||
      (app.company_name === companyData?.company_name && app.position_name === role.name);
    return isMatchingJob;
  });

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!role.company_id) {
        setLoading(false);
        return;
      }

      try {
        const response = await axiosInstance.get(`/api/v1/company/${role.company_id}`);
        setCompanyData(response.data.company);
      } catch (error) {
        console.error('Error fetching company data:', error);
        // Don't throw the error, just log it and continue
        // The component should still render without company data
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [role.company_id]);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  const handleAddApplication = () => {
    setisAddApplicationModalOpen(true);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Split the date string to get only the date part (YYYY-MM-DD)
    const datePart = dateString.split('T')[0];
    const date = new Date(datePart);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC', // Force UTC to prevent timezone conversion
    });
  };

  // Get first two letters of company name for fallback logo
  const getInitials = (name) => {
    if (!name) return 'CO';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatLocations = (locations) => {
    if (!locations) return { display: '', full: '' };
    const locationList = locations.split(',').map((loc) => loc.trim());
    let prefix = '';

    // Check for Multi Location or similar prefixes, including those with colons
    const multiLocationPrefixes = ['Multi Location', 'Multiple Locations', 'Various Locations'];
    const firstLocation = locationList[0];

    // Check if the first item contains a prefix with colon
    const colonIndex = firstLocation.indexOf(':');
    if (colonIndex !== -1) {
      const possiblePrefix = firstLocation.substring(0, colonIndex).trim();
      if (multiLocationPrefixes.some((p) => possiblePrefix.includes(p))) {
        prefix = possiblePrefix;
        // Remove the prefix and colon, keep the rest of the first location
        locationList[0] = firstLocation.substring(colonIndex + 1).trim();
      }
    } else {
      // Check for prefix without colon (old format)
      for (const multiPrefix of multiLocationPrefixes) {
        if (firstLocation === multiPrefix) {
          prefix = multiPrefix;
          locationList.shift();
          break;
        }
      }
    }

    // Filter out any empty locations after processing
    const filteredLocations = locationList.filter((loc) => loc.length > 0);
    const remainingCount = filteredLocations.length > 3 ? filteredLocations.length - 3 : 0;
    const displayLocations = filteredLocations.slice(0, 3).join(', ');

    return {
      prefix,
      display: displayLocations,
      full: filteredLocations.join(', '),
      hasMore: filteredLocations.length > 3,
      count: remainingCount,
    };
  };

  const locations = formatLocations(role.location);

  // Prepare role data for the modal
  const prefilledRoleData = companyData
    ? {
        ...role,
        company: companyData,
      }
    : null;

  return (
    <>
      <div className="group border border-gray-200 hover:border-gray-300 rounded-lg transition-all duration-300">
        <div className="bg-white rounded-lg">
          <div className="p-4">
            <div className="flex items-start gap-4">
              {/* Company Logo */}
              <div className="shrink-0">
                {companyData?.logo_url ? (
                  <div className="relative">
                    <img
                      src={companyData.logo_url}
                      alt={`${companyData.company_name} logo`}
                      className="w-12 h-12 rounded-lg object-contain bg-gray-50"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallbackElement = e.target.nextSibling;
                        if (fallbackElement) {
                          fallbackElement.style.display = 'flex';
                        }
                      }}
                    />
                    <div className="hidden w-12 h-12 rounded-lg bg-blue-50 text-blue-600 font-semibold items-center justify-center">
                      {getInitials(companyData.company_name)}
                    </div>
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-blue-50 text-blue-600 font-semibold flex items-center justify-center">
                    {getInitials(companyData?.company_name)}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-800">{role.name}</h3>
                    </div>
                    <div className="flex items-center flex-wrap gap-x-2 mt-1 text-sm">
                      {companyData?.company_name && (
                        <span className="font-medium text-gray-600">
                          {companyData.company_name}
                        </span>
                      )}
                      {role.categories &&
                        role.categories.map((category, index) => (
                          <React.Fragment key={index}>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-500">{category}</span>
                          </React.Fragment>
                        ))}
                      {locations.display && (
                        <>
                          <span className="text-gray-400">•</span>
                          <div className="relative group/tooltip inline-flex items-center max-w-[400px] gap-1">
                            <svg
                              className="w-3.5 h-3.5 shrink-0 text-gray-500"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            <div className="text-gray-500 truncate">
                              {locations.prefix && (
                                <span className="font-semibold text-blue-600">
                                  {locations.prefix}:{' '}
                                </span>
                              )}
                              <span>{locations.display}</span>
                              {locations.hasMore && (
                                <span className="text-blue-600 font-medium">
                                  {' '}
                                  +{locations.count}
                                </span>
                              )}
                            </div>
                            {(locations.hasMore || locations.prefix) && (
                              <div className="absolute left-1/2 bottom-full mb-2 hidden group-hover/tooltip:block z-10 -translate-x-1/2">
                                <div className="bg-white text-gray-900 text-sm rounded-lg py-2.5 px-4 shadow-lg border border-gray-200 max-w-md whitespace-pre-wrap relative">
                                  <div className="max-h-[200px] overflow-y-auto">
                                    {locations.full}
                                  </div>
                                  <div className="absolute left-1/2 top-full w-4 h-4 bg-white border-b border-r border-gray-200 transform rotate-45 -translate-x-1/2 -mt-2"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                      {role.workModel && (
                        <>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-500 inline-flex items-center">
                            <svg
                              className="w-3.5 h-3.5 mr-1"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                            {role.workModel}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {hasApplied ? (
                      <Link
                        to="/application-tracking"
                        className="py-2.5 px-3 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 focus:outline-hidden focus:bg-blue-100"
                      >
                        View in tracker
                        <svg
                          className="shrink-0 size-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </Link>
                    ) : (
                      <button
                        onClick={handleAddApplication}
                        className="py-2.5 px-3 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 focus:outline-hidden focus:bg-blue-100"
                        ref={tutorialRefs?.addApplication}
                      >
                        Add to tracker
                      </button>
                    )}

                    {role.link && (
                      <button
                        onClick={() => window.open(role.link, '_blank')}
                        className="py-2.5 px-3 inline-flex justify-center items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 focus:outline-hidden focus:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none"
                        ref={tutorialRefs?.applyNow}
                      >
                        Application link
                        <svg
                          className="shrink-0 size-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Additional Info */}
                <div className="mt-2 flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center gap-4">
                    {role.post_date && (
                      <span className="inline-flex items-center">
                        <svg
                          className="w-3.5 h-3.5 mr-1"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        Posted: {formatDate(role.post_date)}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="inline-flex items-center gap-x-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    See role details
                    <svg
                      className={`shrink-0 size-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Expanded Details Section */}
          {isExpanded && (
            <div className="border-t border-gray-200 bg-gray-50">
              <div className="px-4 py-6 space-y-6">
                {/* Role Details Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Role Details</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Basic Information */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Basic Information
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Description
                          </label>
                          <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200 min-h-10">
                            {role.description || 'No description provided'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Location
                          </label>
                          <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                            {role.location || 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Application Link
                          </label>
                          <div className="bg-white px-3 py-2 rounded-lg border border-gray-200">
                            {role.link ? (
                              <ExternalLink
                                href={role.link}
                                className="text-sm text-blue-600 hover:text-blue-800 break-all"
                              >
                                {role.link}
                              </ExternalLink>
                            ) : (
                              <span className="text-sm text-gray-500">No link provided</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Requirements */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Requirements</h4>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Job Focus
                            </label>
                            <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                              {role.job_focus || 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Work Location
                            </label>
                            <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                              {role.work_location || 'Not specified'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Visa Status
                            </label>
                            <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                              {role.visa_status || 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Housing Status
                            </label>
                            <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                              {role.housing_status || 'Not specified'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Duration (weeks)
                            </label>
                            <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                              {role.duration_weeks || 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Required GPA
                            </label>
                            <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                              {role.required_gpa || 'Not specified'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Education Level
                            </label>
                            <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                              {role.min_education_level || 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Internship Season
                            </label>
                            <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                              {role.internship_season || 'Not specified'}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Graduation Year Range
                            </label>
                            <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                              {role.min_graduation_year || role.max_graduation_year
                                ? `${role.min_graduation_year || 'Any'} - ${role.max_graduation_year || 'Any'}`
                                : 'Not specified'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1">
                              Application Deadline
                            </label>
                            <p className="text-sm text-gray-900 bg-white px-3 py-2 rounded-lg border border-gray-200">
                              {role.application_deadline
                                ? formatDate(role.application_deadline)
                                : 'Not specified'}
                            </p>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            Security Clearance
                          </label>
                          <div className="flex items-center bg-white px-3 py-2 rounded-lg border border-gray-200">
                            <div
                              className={`w-2 h-2 rounded-full mr-2 ${role.requires_security_clearance ? 'bg-green-500' : 'bg-gray-300'}`}
                            ></div>
                            <span className="text-sm text-gray-900">
                              {role.requires_security_clearance ? 'Required' : 'Not required'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Skills & Technologies Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Skills & Technologies
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Skills */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Required Skills</h4>
                      <div className="bg-white px-3 py-3 rounded-lg border border-gray-200 min-h-12">
                        {role.skills && role.skills.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {role.skills.map((skill, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">No specific skills listed</span>
                        )}
                      </div>
                    </div>

                    {/* Programming Languages */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Programming Languages
                      </h4>
                      <div className="bg-white px-3 py-3 rounded-lg border border-gray-200 min-h-12">
                        {role.programming_languages && role.programming_languages.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {role.programming_languages.map((lang, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                              >
                                {lang}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">
                            No programming languages specified
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Frameworks & Tools */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Frameworks & Tools
                      </h4>
                      <div className="bg-white px-3 py-3 rounded-lg border border-gray-200 min-h-12">
                        {role.frameworks_and_tools && role.frameworks_and_tools.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {role.frameworks_and_tools.map((tool, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                              >
                                {tool}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">
                            No frameworks or tools specified
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Company Size */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Company Size</h4>
                      <div className="bg-white px-3 py-3 rounded-lg border border-gray-200 min-h-12 flex items-center">
                        <span className="text-sm text-gray-900">
                          {role.company_size || 'Not specified'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Information Section */}
                {companyData && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">
                      Company Information
                    </h3>
                    <div className="bg-white px-4 py-4 rounded-lg border border-gray-200">
                      <div className="flex items-start gap-4">
                        {companyData.logo_url && (
                          <img
                            src={companyData.logo_url}
                            alt={`${companyData.company_name} logo`}
                            className="w-12 h-12 rounded-lg object-contain bg-gray-50"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="text-base font-semibold text-gray-800 mb-2">
                            {companyData.company_name}
                          </h4>
                          {companyData.linkedin_id && (
                            <ExternalLink
                              href={`https://linkedin.com/company/${companyData.linkedin_id}`}
                              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                            >
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.338 16.338H13.67V12.16c0-.995-.017-2.277-1.387-2.277-1.39 0-1.601 1.086-1.601 2.207v4.248H8.014v-8.59h2.559v1.174h.037c.356-.675 1.227-1.387 2.526-1.387 2.703 0 3.203 1.778 3.203 4.092v4.711zM5.005 6.575a1.548 1.548 0 11-.003-3.096 1.548 1.548 0 01.003 3.096zm-1.337 9.763H6.34v-8.59H3.667v8.59zM17.668 1H2.328C1.595 1 1 1.581 1 2.298v15.403C1 18.418 1.595 19 2.328 19h15.34c.734 0 1.332-.582 1.332-1.299V2.298C19 1.581 18.402 1 17.668 1z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              View on LinkedIn
                            </ExternalLink>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {isModalOpen && (
            <OnGlance
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              role={{ ...role, company: companyData }}
            />
          )}

          {isAddApplicationModalOpen && (
            <ApplicationTrackerContext.Provider
              value={{
                setAddApplicationModalOpen: setisAddApplicationModalOpen,
                setRefreshKey: () => {},
                initialValues: {}, // Keep empty as we're using prefilledRole instead
              }}
            >
              <AddApplicationModal
                prefilledRole={prefilledRoleData}
                onClose={() => setisAddApplicationModalOpen(false)}
              />
            </ApplicationTrackerContext.Provider>
          )}
        </div>
      </div>
    </>
  );
}

export default JobComponent;
